import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  type MRT_PaginationState,
} from "material-react-table";
import {
  Button,
  IconButton,
  MenuItem,
  Tooltip,
  Chip,
  Link,
  Box,
  Typography,
  Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import PrecisionManufacturingRoundedIcon from "@mui/icons-material/PrecisionManufacturingRounded";
import DnsRoundedIcon from "@mui/icons-material/DnsRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CircleIcon from "@mui/icons-material/Circle";
import Title from "../../components/Title";
import { debounce } from "lodash";
import { getNotificationsByUser } from "../../features/slices/Notification";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import Back from "../../components/Back";
import {
  Notification,
  CategoryColors,
  PriorityColors,
  SourceColors,
} from "../../interfaces";
import { formatUtcDate } from "../../utils/formatDate";
import FormattedDateCell from "../../components/FormattedDateCell";

const CATEGORY_ICON: Record<string, React.ReactElement> = {
  success: <CheckCircleRoundedIcon />,
  warning: <WarningRoundedIcon />,
  error: <ErrorRoundedIcon />,
  info: <InfoRoundedIcon />,
};

const SOURCE_ICON: Record<string, React.ReactElement> = {
  agent: <PrecisionManufacturingRoundedIcon />,
  system: <DnsRoundedIcon />,
  user: <PersonRoundedIcon />,
};

const SoftChip = ({
  label,
  color,
  icon,
}: {
  label?: string;
  color: string;
  icon?: React.ReactElement;
}) => (
  <Chip
    size="small"
    icon={icon}
    label={label || "—"}
    sx={{
      height: 22,
      fontWeight: 700,
      fontSize: "0.68rem",
      letterSpacing: 0.2,
      textTransform: "capitalize",
      color,
      bgcolor: alpha(color, 0.12),
      border: `1px solid ${alpha(color, 0.32)}`,
      "& .MuiChip-icon": { color, fontSize: 14, ml: "6px" },
      "& .MuiChip-label": { px: 0.9 },
    }}
  />
);

const Notifications = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [data, setData] = useState<Notification[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<MRT_SortingState>([
    {
      id: "id",
      desc: true,
    },
  ]);
  const { user, isInit } = useSelector((state: RootState) => state.auth);

  // const navigate = useNavigate();

  const fetchData = useCallback(
    debounce(async () => {
      if (!user || isInit) return;

      setIsLoading(true);
      setIsRefetching(true);
      //   const params = {
      //     page: pagination.pageIndex + 1,
      //     page_size: pagination.pageSize,
      //     sort_by: sorting[0]?.id || "id",
      //     order: sorting[0]?.desc ? "desc" : "asc",
      //     filter_by: JSON.stringify(columnFilters) || "",
      //     globalFilter: globalFilter || "",
      //   };

      //   const query = new URLSearchParams(params).toString();
      try {
        const data = await dispatch(getNotificationsByUser(user.id)).unwrap();
        // setRowCount(data.total);
        setData(data);
      } catch (error) {
        setIsRefetching(false);
        setIsError(true);
        setIsLoading(false);
      } finally {
        setIsRefetching(false);
        setIsLoading(false);
      }
    }, 300),
    [
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
      globalFilter,
      columnFilters,
      dispatch,
      user,
    ]
  );

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    columnFilters,
    globalFilter,
  ]);

  const columns = useMemo<MRT_ColumnDef<Notification>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
      },
      {
        accessorKey: "user_id",
        header: "User ID",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: "title",
        header: "Title",
        size: 220,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <Tooltip title={row.original.title || ""}>
            <Typography
              sx={{
                fontWeight: row.original.is_read ? 500 : 700,
                fontSize: "0.82rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 220,
              }}
            >
              {row.original.title}
            </Typography>
          </Tooltip>
        ),
      },
      {
        accessorKey: "message",
        header: "Message",
        size: 300,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>() || ""}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 300,
              }}
            >
              {cell.getValue<string>()}
            </Typography>
          </Tooltip>
        ),
      },
      {
        accessorKey: "is_read",
        header: "Read",
        size: 90,
        Cell: ({ cell }) =>
          cell.getValue() ? (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: "text.disabled" }}>
              <CircleIcon sx={{ fontSize: 8 }} />
              <Typography variant="caption">Read</Typography>
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: "error.main", fontWeight: 700 }}>
              <CircleIcon sx={{ fontSize: 8 }} />
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Unread
              </Typography>
            </Stack>
          ),
      },
      {
        accessorKey: "category",
        header: "Category",
        size: 120,
        filterVariant: "select",
        Cell: ({ row }) => (
          <SoftChip
            label={row.original.category}
            color={row.original.category ? CategoryColors[row.original.category] : CategoryColors.default}
            icon={row.original.category ? CATEGORY_ICON[row.original.category] : undefined}
          />
        ),
      },
      {
        accessorKey: "priority",
        header: "Priority",
        size: 110,
        filterVariant: "select",
        Cell: ({ row }) => (
          <SoftChip
            label={row.original.priority}
            color={row.original.priority ? PriorityColors[row.original.priority] : PriorityColors.default}
          />
        ),
      },
      // {
      //   accessorKey: "attachment_url",
      //   header: "Attachment URL",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   Cell: ({ row }) =>
      //     row.original.attachment_url ? (
      //       <Link
      //         href={row.original.attachment_url}
      //         target="_blank"
      //         rel="noopener"
      //       >
      //         View
      //       </Link>
      //     ) : (
      //       "No Attachment"
      //     ),
      // },
      {
        accessorKey: "source",
        header: "Source",
        size: 110,
        filterVariant: "select",
        Cell: ({ row }) => (
          <SoftChip
            label={row.original.source}
            color={row.original.source ? SourceColors[row.original.source] : SourceColors.default}
            icon={row.original.source ? SOURCE_ICON[row.original.source] : undefined}
          />
        ),
      },
      // {
      //   accessorKey: "expires_at",
      //   header: "Expires At",
      // },
      {
        accessorKey: "created_at",
        header: "Created At",
        enableColumnFilter: false,
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
      {
        accessorKey: "updated_at",
        header: "Updated At",
        enableColumnFilter: false,
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.paper,
    }),
    muiTablePaperProps: {
      elevation: 0,
      sx: { borderRadius: 2, border: "1px solid", borderColor: "divider", overflow: "hidden" },
    },
    muiTableContainerProps: {
      sx: { maxHeight: "calc(78vh - 112px)" },
    },
    muiTableHeadCellProps: {
      sx: {
        bgcolor: "background.level1",
        fontWeight: 800,
        fontSize: "0.72rem",
        letterSpacing: 0.4,
        textTransform: "uppercase",
        color: "text.secondary",
      },
    },
    muiTableBodyRowProps: { sx: { "&:hover td": { bgcolor: "action.hover" } } },
    muiTableBodyCellProps: { sx: { borderColor: "divider", py: 0.75 } },

    muiPaginationProps: {
      color: "secondary",
      rowsPerPageOptions: [10, 25, 50, 100],
      shape: "rounded",
      variant: "outlined",
    },
    paginationDisplayMode: "pages",
    columnFilterDisplayMode: "popover",

    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      density: "compact",
      showGlobalFilter: true,
      columnPinning: {
        right: ["mrt-row-actions"],
      },
      sorting: [
        {
          id: "created_at",
          desc: false,
        },
      ],
    },
    // manualFiltering: true,
    // manualPagination: true,
    // manualSorting: true,
    enableColumnResizing: true,
    enableGrouping: false,
    enableRowSelection: false,
    enableRowActions: true,
    enableColumnOrdering: false,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    positionActionsColumn: "last",
    enableFacetedValues: true,

    getRowId: (row) => String(row.id),
    layoutMode: "grid-no-grow",
    muiToolbarAlertBannerProps: isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    rowCount,
    state: {
      isLoading,
      pagination,
      columnFilters,
      globalFilter,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Tooltip arrow title="Refresh Data">
        <IconButton onClick={() => fetchData()}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => [
      <MenuItem
        key={0}
        onClick={() => {
          closeMenu();
        }}
        sx={{ m: 0 }}
      >
        Delete
      </MenuItem>,
    ],
  });

  return (
    <>
      <Title>Notifications Overview</Title>
      <MaterialReactTable table={table} />
      <Back />
    </>
  );
};

export default Notifications;
